import { forwardRef, useContext } from 'react';
import { cn } from '@/utils/cn';
import type { InputProps, InputVariant } from './Input.types';
import { tokens } from '@/theme/tokens';
import { ThemeContext } from '@/context/ThemeContext';

/**
 * # Input
 *
 * Zakladni textovy vstup pro formulare.
 * Podporuje varianty, velikosti, validacni stavy a ikony.
 *
 * @example
 * <Input label="Email" type="email" error="Neplatny email" />
 * <Input size="lg" leftIcon={<Search />} placeholder="Hledat..." />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      size = 'md',
      variant = 'default',
      leftIcon,
      rightIcon,
      fullWidth = true,
      className,
      id,
      required,
      disabled,
      'data-testid': testId,
      ...nativeProps
    },
    ref,
  ) => {
    // Generovany ID pro label-input propojeni
    const inputId = id || `input-${label?.toLowerCase().replace(/\s+/g, '-')}`;
    const hasError = !!error;
    const theme = useContext(ThemeContext);
    const isDark = theme?.mode === 'dark';
    const themedForegroundClass = isDark ? 'ui-field-foreground-dark' : 'ui-field-foreground-light';

    const variantThemeClasses: Record<InputVariant, string> = {
      default: isDark
        ? 'border-white/10 bg-zinc-950/72 text-zinc-100'
        : 'border-emerald-100/80 bg-white/92 text-gray-900',
      ghost: isDark
        ? 'border-transparent bg-white/5 text-zinc-100'
        : 'border-transparent bg-white/60 text-gray-900',
    };

    const errorThemeClasses = isDark
      ? 'border-red-400 bg-red-950/25 text-zinc-100'
      : 'border-red-500 bg-red-50/90 text-gray-900';

    const disabledThemeClasses = isDark ? 'bg-zinc-800/50' : 'bg-gray-50';

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'text-sm font-medium',
              'text-gray-700 dark:text-zinc-200',
              disabled && 'opacity-50',
            )}
          >
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        {/* Input wrapper (pro ikony) */}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500 pointer-events-none">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-describedby={hasError ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
            data-testid={testId}
            {...(hasError ? { 'aria-invalid': true } : {})}
            className={cn(
              // Base
              'w-full border outline-none shadow-sm',
              tokens.transition.fast,
              'placeholder:text-gray-400 dark:placeholder:text-zinc-500',
              themedForegroundClass,
              tokens.radius.sm,
              // Size
              sizeClasses[size],
              // Icon padding
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              // Variant/State
              hasError ? errorThemeClasses : variantThemeClasses[variant],
              // Disabled
              disabled && 'opacity-50 cursor-not-allowed',
              disabled && disabledThemeClasses,
              className,
            )}
            {...nativeProps}
          />

          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-zinc-500">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Error / Hint zprava */}
        {hasError && (
          <p
            id={`${inputId}-error`}
            className="text-sm text-red-500 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {!hasError && hint && (
          <p id={`${inputId}-hint`} className="text-sm text-gray-500 dark:text-zinc-400">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

// --- Style mapy (MIMO komponentu pro citelnost) ---

const sizeClasses: Record<string, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-3.5 py-2 text-sm',
  lg: 'px-4 py-2.5 text-base',
};

